import React, { useEffect } from "react";
import {
  FloatingHelper,
  FormField,
  PulseAnimation,
  EmptyState,
  CustomModalLayout,
  TextButton,
  Heading,
  Modal,
  MessageModalLayout,
  Text,
  Card,
  Box,
  Page,
  Button,
  Cell,
  Layout,
  Input,
  Image,
  PageSection,
  Badge,
  MarketingLayout,
  AnnouncementModalLayout,
  MarketingPageLayoutContent,
  Thumbnail,
} from "@wix/design-system";
import * as Icons from "@wix/wix-ui-icons-common/lazy";
import "./App.css";
import { ReactTagManager } from "react-gtm-ts";
import { useTranslation } from "react-i18next";
import { identify, track, WIX_APP_ID } from "./analytics";

const tagManagerArgs = {
  code: "GTM-WZQPMC7",
};

function App() {
  const { t } = useTranslation();
  const upgradeUrl = `https://memberships.certifiedcode.us/${window.location.search}`;
  const [isHighlighted, setIsHighlighted] = React.useState(false);
  const [isUpgraded, setIsUpgraded] = React.useState(false);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [isOpening, setIsOpening] = React.useState(false);
  const [shownSettingsModal, setShownSettingsModal] = React.useState(false);
  const [merchantId, setMerchantId] = React.useState("");
  const [hashKey, setHashKey] = React.useState("");
  const [hashIV, setHashIV] = React.useState("");
  const [isBusinessAppModalOpened, setIsBusinessAppModalOpened] =
    React.useState(false);
  const [instanceData, setInstanceData] = React.useState({
    instance: {
      isFree: true,
      availablePlans: [],
      instanceId: "",
    },
  });

  useEffect(() => {
    ReactTagManager.init(tagManagerArgs);
    const i = new URLSearchParams(window.location.search).get("instance");
    identify(i);
    track("dashboard_opened");
    setInstance();
    getSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const token = new URLSearchParams(window.location.search).get("token");
  const instance = new URLSearchParams(window.location.search).get("instance");
  const trackingParams = new URLSearchParams(window.location.search);
  const trackedUtmKeys = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
    "utm_id",
    "utm_source_platform",
    "utm_creative_format",
    "utm_marketing_tactic",
  ];

  if (token) {
    track("install_redirect");
    window.location.href = `https://www.wix.com/installer/install?token=${token}&appId=32f8605b-3c95-447a-9f2c-4e6352ba3e48&redirectUrl=https://wix.certifiedcode.io/ecpay-pay-button/_functions/@certifiedcode/base-backend/auth`;
  }

  // if (!instance) {
  //   return null
  // }

  const BASE_URL = `https://api.certifiedco.de/apps/32f8605b-3c95-447a-9f2c-4e6352ba3e48`;

  function getPricingUrl() {
    const redirectUrl = new URL("https://www.flows.tw/pricing");

    trackedUtmKeys.forEach((key) => {
      const value = trackingParams.get(key);
      if (value) {
        redirectUrl.searchParams.set(key, value);
      }
    });

    return redirectUrl.toString();
  }

  const pricingUrl = getPricingUrl();


  function setInstance() {
    fetch(BASE_URL + "/@certifiedcode/base-backend/instance", {
      method: "GET",
      headers: {
        Authorization: instance || "",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        console.log(data);
        setInstanceData(data);
        setIsUpgraded(data?.instance?.isFree === false);
      })
      .catch(() => { });
  }

  function setSettings() {
    if (isUpgraded) {
      if (!open) {
        if (merchantId === "" || hashKey === "" || hashIV === "") {
          setShownSettingsModal(true);
          return;
        }
      }
      const account =
        !open === true
          ? {
            merchantId: merchantId,
            hashKey: hashKey,
            hashIV: hashIV,
          }
          : {};
      setIsOpening(true);
      fetch(BASE_URL + "/settings", {
        method: "POST",
        headers: {
          Authorization: instance || "",
        },
        body: JSON.stringify({
          isEnabled: !open,
          ...account,
        }),
      }).then(() => {
        setShownSettingsModal(false);
        setIsOpening(false);
        setOpen(!open);
      });
    } else {
      setIsUpgradeModalOpen(true);
    }
  }

  function getSettings() {
    setIsOpening(true);
    fetch(BASE_URL + "/settings", {
      method: "GET",
      headers: {
        Authorization: instance || "",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        setIsOpening(false);
        setOpen(data.isEnabled);
      })
      .catch(() => { });
  }

  const isPremiumApp = true;

  const renderThumbnail = ({ title, subtitle }: any) => (
    <a
      href={pricingUrl}
      target="_blank"
      rel="noreferrer"
      style={{ textDecoration: "none", color: "inherit", display: "block" }}
    >
      <Thumbnail>
        <Box padding="18px">
          <Box gap="12px" verticalAlign="middle">
            <Icons.Premium />
            <Box direction="vertical">
              <Text size="medium" weight="bold">
                {title}
              </Text>
              <Box>
                <Text size="small" secondary>
                  {subtitle}
                </Text>
              </Box>
            </Box>
          </Box>
        </Box>
      </Thumbnail>
    </a>
  );

  return (
    <>
      <Page height="100vh">
        <Page.Header
          title={
            <Box gap={"SP2"} verticalAlign="middle">
              <Heading>{t("heading")}</Heading>
            </Box>
          }
          actionsBar={
            <Box gap={"SP2"} direction="horizontal">
              {isPremiumApp ? (
                <Button
                  skin="premium"
                  prefixIcon={<Icons.PremiumFilled />}
                  onClick={() => setIsUpgradeModalOpen(true)}
                >
                  {isUpgraded ? t("manage-plan") : t("accept-cta")}
                </Button>
              ) : (
                <></>
              )}
            </Box>
          }
        />
        <Page.Content>
          <Modal
            isOpen={isBusinessAppModalOpened}
            onRequestClose={() => setIsBusinessAppModalOpened(false)}
            shouldCloseOnOverlayClick
            screen="desktop"
          >
            <CustomModalLayout
              onCloseButtonClick={() => {
                setIsBusinessAppModalOpened(false);
              }}
              closeButtonProps={{ skin: "dark", size: "small" }}
              showHeaderDivider={false}
              showFooterDivider={false}
              title={t("advanced-title")}
              content={
                <Card>
                  <MarketingPageLayoutContent
                    title={t("advanced-desc")}
                    content={
                      <Text>
                        <ul>
                          <li>{t("li-1")}</li>
                          <li>{t("li-2")}</li>
                          <li>{t("li-3")}</li>
                          <Box
                            alignContent="center"
                            gap="SP1"
                            direction="horizontal"
                          >
                            <li>{t("li-4")}</li>
                          </Box>
                        </ul>
                      </Text>
                    }
                    actions={
                      <Layout>
                        <Cell>
                          <Layout cols={1} gap="12px">
                            <Text size="small" weight="bold">
                              {t("plan-heading")}
                            </Text>
                            {renderThumbnail({
                              title: t("lifetime-title"),
                              subtitle: t("lifetime-subtitle"),
                            })}
                            {renderThumbnail({
                              title: t("annual-title"),
                              subtitle:
                                t("annual-subtitle"),
                            })}
                            {renderThumbnail({
                              title: t("monthly-title"),
                              subtitle:
                                t("monthly-subtitle"),
                            })}
                          </Layout>
                        </Cell>
                        <Cell>
                          <Box gap="SP2">
                            <Button
                              prefixIcon={<Icons.Help />}
                              skin="inverted"
                              as="a"
                              href="https://www.flows.tw/pricing"
                              target="_blank"
                            >
                              {t("learn-integration")}
                            </Button>
                            <Button
                              prefixIcon={<Icons.Phone />}
                              skin="light"
                              onClick={() => {
                                window.Intercom(
                                  "showNewMessages",
                                  t("msg-integration")
                                );
                              }}
                            >
                              {t("talk-sales")}
                            </Button>
                          </Box>
                        </Cell>
                        <Cell>
                          <Text size="tiny">
                            {t("sale-tax-msg")}
                            <a
                              href="https://www.certifiedcode.us/legal/terms-of-services"
                              target="_blank"
                            >
                              {t("tos-link")}
                            </a>
                          </Text>
                        </Cell>
                        <Cell></Cell>
                      </Layout>
                    }
                  />
                </Card>
              }
            />
          </Modal>
          <Modal
            isOpen={shownSettingsModal}
            onRequestClose={() => setShownSettingsModal(false)}
            shouldCloseOnOverlayClick
            screen="desktop"
          >
            <CustomModalLayout
              primaryButtonText={t("save-button")}
              secondaryButtonText={t("cancel-button")}
              onCloseButtonClick={() => setShownSettingsModal(false)}
              closeButtonProps={{ skin: "dark", size: "small" }}
              showHeaderDivider={false}
              showFooterDivider={false}
              secondaryButtonOnClick={() => setShownSettingsModal(false)}
              primaryButtonOnClick={() => setSettings()}
              removeContentPadding
              title={t("connect-title")}
              content={
                <Page>
                  <Page.Content>
                    <Box marginTop={5} display="block">
                      <Layout>
                        <Cell span={12}>
                          <Box direction="vertical" gap="SP2">
                            <FormField
                              label={t("merchant-label")}
                              infoContent={t("merchant-placeholder")}
                            >
                              <Input
                                clearButton
                                value={merchantId}
                                onChange={(e) => setMerchantId(e.target.value)}
                              />
                            </FormField>
                            <FormField label="HashKey">
                              <Input
                                clearButton
                                value={hashKey}
                                onChange={(e) => setHashKey(e.target.value)}
                              />
                            </FormField>
                            <FormField label="HashIV">
                              <Input
                                clearButton
                                value={hashIV}
                                onChange={(e) => setHashIV(e.target.value)}
                              />
                            </FormField>
                          </Box>
                        </Cell>
                      </Layout>
                    </Box>
                  </Page.Content>
                </Page>
              }
            />
          </Modal>
          <Modal
            isOpen={isUpgradeModalOpen}
            shouldCloseOnOverlayClick={true}
            onRequestClose={() => setIsUpgradeModalOpen(false)}
          >
            <AnnouncementModalLayout
              theme="premium"
              title={t("upgrade-title")}
              primaryButtonText={t("upgrade-button")}
              linkText={t("learn-more")}
              primaryButtonOnClick={() => {
                window.open(
                  upgradeUrl
                );
              }}
              linkOnClick={() => {
                window.Intercom("showArticle", 7851533);
              }}
              onCloseButtonClick={() => setIsUpgradeModalOpen(false)}
              closeButtonProps={{ skin: "dark", size: "small" }}
            >
              <Text>{t("one-price-msg")}</Text>
            </AnnouncementModalLayout>
          </Modal>
          <Modal isOpen={isModalOpen} screen="desktop">
            <MessageModalLayout
              primaryButtonText={t("refresh-button")}
              primaryButtonOnClick={() => window.location.reload()}
              title={t("error-title")}
              content={<Text>{t("refresh-text")}</Text>}
            />
          </Modal>
          <Layout>
            <Cell span={12}>
              <Card>
                <Card.Header
                  title={
                    <Box gap={"SP2"}>
                      <Heading size="medium" as="h2">
                        {t("online-heading")}
                      </Heading>
                      {isUpgraded ? (
                        <Badge size="tiny" skin={"success"}>{t("ready-badge")}</Badge>
                      ) : (
                        <FloatingHelper
                          target={
                            <Badge size="tiny" skin={"danger"}>{t("limited-badge")}</Badge>
                          }
                          content={
                            <FloatingHelper.Content
                              title={t("popup-title")}
                              body={t("trial-body")}
                            />
                          }
                          placement="right"
                        />
                      )}
                    </Box>
                  }
                  suffix={
                    <Button
                      onClick={() => {
                        setSettings();
                      }}
                      size="medium"
                      skin={open && isUpgraded ? "inverted" : "premium"}
                      disabled={isOpening}
                    >
                      {open && isUpgraded ? t("disconnect-account") : t("connect-account")}
                    </Button>
                  }
                />
                <Card.Subheader
                  title={
                    <Text size="small">
                      {t("paybtn-desc")}
                      <b>{t("no-ecom-note")}</b>
                    </Text>
                  }
                  suffix={
                    <Button
                      skin="light"
                      size="small"
                      onClick={() => {
                        setIsHighlighted(true);
                      }}
                    >
                      {t("need-stores")}
                    </Button>
                  }
                />
                <Card.Content>
                  {open && isUpgraded ? (
                    <EmptyState
                      theme="section"
                      title={t("live-title")}
                      subtitle={t("live-subtitle")}
                    >
                      <TextButton
                        prefixIcon={<Icons.ExternalLink />}
                        as={"a"}
                        href={
                          (instanceData as any)["site"]
                            ? (instanceData as any)["site"]["url"]
                            : ""
                        }
                        target={"_blank"}
                      >
                        {t("live-button")}
                      </TextButton>
                    </EmptyState>
                  ) : (
                    <></>
                  )}
                </Card.Content>
              </Card>
            </Cell>
            <Cell>
              <PageSection title={t("suggested-title")} showDivider={false} />
            </Cell>
            <Cell span={12}>
              <Card>
                <PulseAnimation
                  active={isHighlighted}
                  color="B10"
                  borderRadius="18px"
                  fluid={true}
                >
                  <MarketingLayout
                    title={t("upgrade-title")}
                    description={t("online-desc")}
                    actions={
                      <Box direction="horizontal" gap="SP1">
                        <Button
                          size="small"
                          skin="premium"
                          onClick={() => {
                            setIsBusinessAppModalOpened(true);
                            setIsHighlighted(false);
                          }}
                        >
                          {t("see-button")}
                        </Button>
                        <Button
                          size="small"
                          skin="inverted"
                          onClick={() => {
                            setIsHighlighted(false);
                            window.Intercom(
                              "showNewMessages",
                              t("msg-accept")
                            );
                          }}
                        >
                          {t("talk-sales")}
                        </Button>
                      </Box>
                    }
                    size="tiny"
                    badge={<Badge size="small">{t("merchants-badge")}</Badge>}
                    image={
                      <Box width="100%" align="right">
                        <Image
                          width="120px"
                          src="https://www.wix-pages.com/wix-design-system-employees/generic_upgrade.svg"
                          transparent
                        />
                      </Box>
                    }
                  />
                </PulseAnimation>
              </Card>
            </Cell>
            {/* <Cell span={6}>
              <Card>
                <MarketingLayout
                  title={t("upgrade-title")}
                  description="透過 Flows. 整合OS，您可以使用本地支付服務商讓客戶使用台灣付款方式。"
                  actions={<Button size="small" onClick={() => { window.open("https://www.flows.tw/payments/ecpay") }}>瞭解更多</Button>}
                  size="tiny"
                  badge={<Badge size="small">第三方整合</Badge>}
                  image={
                    <Box width="100%" align="right">
                      <Image
                        width="120px"
                        src="https://www.wix-pages.com/wix-design-system-employees/generic_upgrade.svg"
                        transparent
                      />
                    </Box>
                  }
                />
              </Card>
            </Cell> */}
            <Cell span={12}>
              <Card>
                <MarketingLayout
                  title={t("contact-title")}
                  description={t("contact-desc")}
                  actions={
                    <Button
                      size="small"
                      skin="light"
                      onClick={() => {
                        window.Intercom(
                          "showNewMessages",
                          t("msg-help")
                        );
                      }}
                    >
                      {t("book-call")}
                    </Button>
                  }
                  size="tiny"
                  hiddenBadge
                  alignItems="stretch"
                  image={
                    <Box width="100%" align="right">
                      <Image
                        width="120px"
                        src="https://www.wix-pages.com/wix-design-system-employees/PromotionalBookingsUpgrade.svg"
                        transparent
                      />
                    </Box>
                  }
                />
              </Card>
            </Cell>
          </Layout>
        </Page.Content>
      </Page>
    </>
  );
}

export default App;
